import { create } from 'zustand';

export type SelectMode = 'replace' | 'toggle' | 'add';

export interface SelectionState {
  /** Angezeigte und bearbeitete Seite. */
  pageIndex: number;
  ids: string[];
  /** Zähler für das Durchwandern überlappender Felder mit Tab. */
  cycleIndex: number;
  /** Feld, dessen Text gerade bearbeitet wird (Phase 2.1). */
  editingId: string | null;
  /** Markierte Zellen einer Tabelle (Phase 2.5). */
  cells: { itemId: string; row: number; col: number }[];
  /** Zelle, in der gerade geschrieben wird. */
  editingCell: { itemId: string; row: number; col: number } | null;
  markCell(itemId: string, row: number, col: number, additive?: boolean): void;
  clearCells(): void;
  startCellEditing(itemId: string, row: number, col: number): void;
  /** Schlüssel, der alle Änderungen einer Texteingabe zu einem Undo-Schritt macht. */
  editingKey: string | null;
  startEditing(id: string): void;
  stopEditing(): void;
  select(ids: readonly string[], mode?: SelectMode): void;
  toggle(id: string): void;
  clear(): void;
  setPage(index: number): void;
  isSelected(id: string): boolean;
  /** Nächstes Feld unter dem Zeiger (Tab bei Überlappung). */
  cycle(candidates: readonly string[]): void;
}

let editCounter = 0;

export const useSelectionStore = create<SelectionState>((set, get) => ({
  pageIndex: 0,
  ids: [],
  cycleIndex: 0,
  editingId: null,
  editingKey: null,
  cells: [],
  editingCell: null,

  markCell: (itemId, row, col, additive = false) =>
    set((state) => {
      const exists = state.cells.some(
        (cell) => cell.itemId === itemId && cell.row === row && cell.col === col,
      );
      if (!additive) return { cells: [{ itemId, row, col }], editingCell: null };
      return {
        cells: exists
          ? state.cells.filter(
              (cell) => !(cell.itemId === itemId && cell.row === row && cell.col === col),
            )
          : [...state.cells.filter((cell) => cell.itemId === itemId), { itemId, row, col }],
        editingCell: null,
      };
    }),

  clearCells: () => set({ cells: [], editingCell: null }),

  startCellEditing: (itemId, row, col) => {
    editCounter += 1;
    set({
      cells: [{ itemId, row, col }],
      editingCell: { itemId, row, col },
      editingKey: `cell-${itemId}-${row}-${col}-${editCounter}`,
    });
  },

  startEditing: (id) => {
    editCounter += 1;
    set({ editingId: id, editingKey: `text-${id}-${editCounter}`, ids: [id] });
  },

  stopEditing: () => set({ editingId: null, editingKey: null }),

  select: (ids, mode = 'replace') =>
    set((state) => {
      // Eine neue Auswahl beendet die Texteingabe.
      const editing =
        state.editingId && ids.length === 1 && ids[0] === state.editingId
          ? {}
          : { editingId: null, editingKey: null };
      if (mode === 'replace') {
        return { ids: [...ids], cycleIndex: 0, cells: [], editingCell: null, ...editing };
      }
      if (mode === 'add') return { ids: [...new Set([...state.ids, ...ids])] };
      const current = new Set(state.ids);
      for (const id of ids) {
        if (current.has(id)) current.delete(id);
        else current.add(id);
      }
      return { ids: [...current] };
    }),

  toggle: (id) => get().select([id], 'toggle'),
  clear: () =>
    set({
      ids: [],
      cycleIndex: 0,
      editingId: null,
      editingKey: null,
      cells: [],
      editingCell: null,
    }),
  setPage: (index) =>
    set({
      pageIndex: index,
      ids: [],
      cycleIndex: 0,
      editingId: null,
      editingKey: null,
      cells: [],
      editingCell: null,
    }),
  isSelected: (id) => get().ids.includes(id),

  cycle: (candidates) =>
    set((state) => {
      if (candidates.length === 0) return state;
      const current = state.ids[0];
      const position = current ? candidates.indexOf(current) : -1;
      const next = candidates[(position + 1) % candidates.length]!;
      return { ids: [next], cycleIndex: position + 1 };
    }),
}));
